import React, { useState, useEffect } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Typography,
  CircularProgress,
  SelectChangeEvent,
  Box
} from '@mui/material';
import axios from 'axios';
import { logger } from '../../utils/logger';

interface ICP {
  id: string;
  title: string;
  data: string;
  default: boolean;
}

interface ICPSelectorProps {
  value: string;
  onChange: (e: SelectChangeEvent) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
}

const ICPSelector: React.FC<ICPSelectorProps> = ({
  value,
  onChange,
  error,
  required = true,
  disabled = false
}) => {
  const [icps, setIcps] = useState<ICP[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIcp, setSelectedIcp] = useState<ICP | null>(null);
  const label = `Audience Persona${required ? ' *' : ''}`;

  useEffect(() => {
    const fetchICPs = async () => {
      try {
        setLoading(true);
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

        const response = await axios.get(`${API_URL}/api/icps`, {
          withCredentials: true
        });
        
        setIcps(response.data.icps || []);
      } catch (error) {
        logger.error('Error fetching ICPs:', error);
        setIcps([]);
      } finally {
        setLoading(false);
      }
    };

    fetchICPs();
  }, []);

  // Update selected ICP when value changes
  useEffect(() => {
    if (value && icps.length > 0) {
      const selected = icps.find(icp => icp.id === value);
      setSelectedIcp(selected || null);
    } else {
      setSelectedIcp(null);
    }
  }, [value, icps]);

  const handleChange = (e: SelectChangeEvent) => {
    const selectedId = e.target.value;
    const selected = icps.find(icp => icp.id === selectedId);
    setSelectedIcp(selected || null);
    onChange(e);
  };

  return (
    <Box>
      <FormControl fullWidth margin="normal">
        <InputLabel id="icp-label">{label}</InputLabel>
        <Select
          labelId="icp-label"
          id="icpId"
          name="icpId"
          value={value || ''}
          label={label}
          onChange={handleChange}
          error={!!error}
          disabled={disabled || loading}
        >
          {loading ? (
            <MenuItem disabled>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Loading Personas...
            </MenuItem>
          ) : icps.length > 0 ? (
            icps.map((icp) => (
              <MenuItem key={icp.id} value={icp.id}>
                {icp.title}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled value="">
              No Personas available
            </MenuItem>
          )}
        </Select>
        {error && (
          <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </FormControl>
      
      {selectedIcp && (
        <Box 
          sx={{ 
            mt: 1, 
            p: 2, 
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 1
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.6 }}>
            {selectedIcp.data}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ICPSelector; 